import { useEffect, useRef } from "react";

import { useWidgetIsReady, useWidgetState } from "@livechat/widget-react";

import { pathnameIsWelcomeTutorialPage } from "routes/welcome-tutorial/helpers";

import { useChatWidget } from "./use-chat-widget";

/**
 * Hides the support chat widget on welcome tutorial pages so it does not overlap
 * footer action buttons. Restores the minimized bubble when leaving those routes.
 *
 * Intended for use inside the app {@link LiveChatWidget} wrapper, which is only mounted when the
 * widget is enabled for the app context. Calls the Widget API only after the widget
 * reports ready via `useWidgetIsReady`.
 */
export const useHideChatWidgetOnWelcomeTutorial = (pathname: string): void => {
  const isWelcomeTutorialPage = pathnameIsWelcomeTutorialPage(pathname);
  const widgetIsReady = useWidgetIsReady();
  const widgetState = useWidgetState();
  const { hideChatWidget, closeChatWidget } = useChatWidget();
  const hiddenByWelcomeTutorialRef = useRef(false);

  useEffect(() => {
    if (!widgetIsReady) {
      return;
    }

    if (isWelcomeTutorialPage) {
      if (widgetState?.visibility !== "hidden") {
        hideChatWidget();
      }
      hiddenByWelcomeTutorialRef.current = true;
      return;
    }

    if (hiddenByWelcomeTutorialRef.current) {
      closeChatWidget();
      hiddenByWelcomeTutorialRef.current = false;
    }
  }, [isWelcomeTutorialPage, widgetIsReady, widgetState?.visibility, hideChatWidget, closeChatWidget]);
};
